import { Popover } from '@headlessui/react';
import React, { useEffect, useState } from 'react';

import { SingleNeutralCircle } from '@videoblocks/react-icons';
import ChevronUpIcon from '@videoblocks/react-icons/ChevronUp';

import { User, MenuList } from '../../../app/Navigation/types';
import MenuVariant from './MenuVariant';
import UserAccountMenu from './UserAccountMenu';

type MenuDropdownProps = {
  isUserOnStarterPlan?: boolean;
  isUserOnGrowthPlan?: boolean;
  shouldShowEnterpriseExperience?: boolean;
  organizationName?: string;
  user: User;
  userInitials?: string;
  starterPlanRemainingDownloads?: number;
  remainingExports?: number;
  account: MenuList;
};
const MenuDropdown = ({
  isUserOnStarterPlan,
  isUserOnGrowthPlan,
  shouldShowEnterpriseExperience,
  organizationName,
  user,
  userInitials,
  starterPlanRemainingDownloads,
  remainingExports,
  account,
}: MenuDropdownProps) => {
  const [isClient, setIsClient] = useState(false);
  const accountLogo = (
    <div className="account-logo h-6 w-6" aria-label="Account logo" role="img">
      <SingleNeutralCircle className="fill-none text-white cursor-pointer" />
    </div>
  );
  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <>
      {/*This will prerender the accountLogo in SSR and prevent any shifts or gaps when client-side code is present.*/}
      {!isClient && <div className="pb-1 relative"> {accountLogo} </div>}
      {isClient &&
        (isUserOnStarterPlan || isUserOnGrowthPlan ? (
          <Popover
            as="div"
            className="account account-member dropdown inline-block text-gray-700 bg-transparent border-none outline-none cursor-pointer"
            id="main-nav-dropdown-trigger"
          >
            {({ open }) => (
              <>
                <Popover.Button
                  data-cy="account-dropdown-toggle"
                  className="account-dropdown-toggle inline-block text-gray-700 hover:text-black  bg-transparent border-none cursor-pointer pt-1 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white"
                >
                  {shouldShowEnterpriseExperience ? (
                    <div
                      id="enterprise-label"
                      className="flex items-center mr-2 ml-2 mb-2 mt-2 font-normal relative"
                    >
                      {accountLogo}
                      <div className="flex flex-col items-start leading-5">
                        <div className="ellipsis max-w-50">
                          {organizationName}
                        </div>
                        <div>Enterprise Account</div>
                      </div>
                      <ChevronUpIcon
                        className={`h-3 w-3 transition-transform inline-block cursor-pointer ${
                          open ? 'transform -rotate-180' : 'rotate-0'
                        }`}
                      />
                    </div>
                  ) : (
                    accountLogo
                  )}
                </Popover.Button>
                <Popover.Panel className="dropdown w-72 shadow-popover absolute right-0 left-auto z-10 py-6 text-sm text-gray-900 bg-white border border-gray-100 border-solid rounded">
                  <div className="flex flex-col items-center">
                    {user && userInitials.length > 0 && (
                      <div className="flex items-center justify-center w-16 h-16 mb-2 text-xl font-bold text-gray-700 bg-gray-300 rounded-full">
                        {userInitials}
                      </div>
                    )}
                    <div className="overflow-ellipsis w-72 whitespace-no-wrap px-6 overflow-hidden text-center">
                      {user.email}
                    </div>
                    <div className="mt-4">
                      Remaining Stock Downloads:{' '}
                      <span id="user-monthly-remaining-downloads">
                        {starterPlanRemainingDownloads}
                      </span>
                    </div>
                    {remainingExports !== null && (
                      <div className="mt-2">
                        Remaining Maker Exports:{' '}
                        <span id="user-monthly-remaining-exports">
                          {remainingExports}
                        </span>
                      </div>
                    )}
                  </div>
                  <hr className="mx-6 mt-6 mb-0 border-gray-300" />
                  <UserAccountMenu
                    menuList={account}
                    variant={MenuVariant.Underline}
                  />
                </Popover.Panel>
              </>
            )}
          </Popover>
        ) : (
          <Popover
            as="div"
            className="account account-member dropdown inline-block text-gray-700 bg-transparent border-none outline-none cursor-pointer fill-current"
            id="account-dropdown"
            aria-label="Account"
          >
            {({ open }) => (
              <>
                <Popover.Button
                  data-cy="account-dropdown-toggle"
                  className="account-dropdown-toggle inline-block text-gray-700 hover:text-black  bg-transparent border-none cursor-pointer fill-current rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white"
                >
                  {shouldShowEnterpriseExperience ? (
                    <div
                      id="enterprise-label"
                      className="flex items-center space-x-2 font-normal"
                    >
                      {accountLogo}
                      <div className="flex flex-col items-start leading-5">
                        <div className="ellipsis max-w-50">
                          {organizationName}
                        </div>
                        <div>Enterprise Account</div>
                      </div>
                      <ChevronUpIcon
                        className={`h-3 w-3 transition-transform inline-block cursor-pointer ${
                          open ? 'transform -rotate-180' : 'rotate-0'
                        }`}
                      />
                    </div>
                  ) : (
                    accountLogo
                  )}
                </Popover.Button>
                <Popover.Panel className="account-menu dropdown w-52 shadow-popover absolute right-0 left-auto z-10 flex flex-col px-0 py-2 text-sm leading-6 bg-white border border-gray-100 border-solid rounded text-center">
                  <UserAccountMenu
                    menuList={account}
                    variant={MenuVariant.Hover}
                  />
                </Popover.Panel>
              </>
            )}
          </Popover>
        ))}
    </>
  );
};

export default MenuDropdown;
